import { InfoCircleTwoTone } from "@ant-design/icons";
import { Checkbox, DatePicker, Form, Select, Tooltip } from "antd";
import dateFormats from "common/data/dateFormats";
import dateSeparators from "common/data/dateSeparators";
import { MessageBox } from "components/atoms/MessageBox";
import SchemaClassifiersFieldProperty from "components/atoms/SchemaClassifiersFieldProperty";
import Segment from "components/atoms/Segment";
import dayjs from "dayjs";
import _ from "lodash";
import { useState } from "react";
import CypressTestIds from "../../../cypress/CypressTestIds";

const { Option } = Select;
const { RangePicker } = DatePicker;

const initialDateFormat = "MM$DD$YYYY";
const initialDateSeparator = "/";
const initialDateFormatOptions = Object.keys(dateFormats).map((key) => ({ name: _.replace(dateFormats[key].name, /\$/g, initialDateSeparator), value: key }));
const initialDateFormatValue = `${_.replace(initialDateFormat, /\$/g, initialDateSeparator)}`;
const initialRangeValues = [dayjs("01/01/1970", initialDateFormatValue), dayjs("01/01/2099", initialDateFormatValue)];

const DateFieldProperties = ({ formRef, selectedFieldRow, readOnly, getFormErrors }) => {
    let currentDateFormat = formRef.current.getFieldValue(["fields", selectedFieldRow.name, "dateFormat"]);
    const currentDateSeparator = formRef.current.getFieldValue(["fields", selectedFieldRow.name, "dateSeparator"]);
    let currentDateFormatValue = null;
    let currentDateFormatOptions = null;
    if (currentDateFormat && currentDateSeparator) {
        currentDateFormatValue = `${_.replace(currentDateFormat, /\$/g, currentDateSeparator)}`;
        currentDateFormatOptions = Object.keys(dateFormats).map((key) => ({ name: _.replace(dateFormats[key].name, /\$/g, currentDateSeparator), value: key }));
    }
    const [dateFormatOptions, setDateFormatOptions] = useState(currentDateFormatOptions || initialDateFormatOptions);
    const [dateExampleValue, setDateExampleValue] = useState(dayjs().format(currentDateFormatValue || initialDateFormatValue));
    const [rangeFormat, setRangeFormat] = useState(currentDateFormatValue || initialDateFormatValue);

    const onDateFormatChange = (dateFormat, selectedFieldRow) => {
        let dateSeparator = formRef.current.getFieldValue(["fields", selectedFieldRow.name, "dateSeparator"]);
        let dateFormatValue = `${_.replace(dateFormat, /\$/g, dateSeparator)}`;
        let dateExampleValue = dayjs().format(dateFormatValue);
        setDateExampleValue(dateExampleValue);
        setRangeFormat(dateFormatValue);
    };

    const onDateSeparatorChange = (dateSeparator, selectedFieldRow) => {
        let dateFormatOptions = Object.keys(dateFormats).map((key) => ({ name: _.replace(dateFormats[key].name, /\$/g, dateSeparator), value: key }));
        setDateFormatOptions(dateFormatOptions);

        let dateFormat = formRef.current.getFieldValue(["fields", selectedFieldRow.name, "dateFormat"]);
        let dateFormatValue = `${_.replace(dateFormat, /\$/g, dateSeparator)}`;

        let dateExampleValue = dayjs().format(dateFormatValue);
        setDateExampleValue(dateExampleValue);
        setRangeFormat(dateFormatValue);
    };

    return (
        <>
            <Segment.Group>
                <Segment className="form-background">
                    <MessageBox info header="Preview">
                        <p>{dateExampleValue}</p>
                    </MessageBox>
                </Segment>
                <Segment className="form-background">
                    <Form.Item
                        label="Date Format"
                        name={["fields", selectedFieldRow.name, "dateFormat"]}
                        initialValue={initialDateFormat}
                        {...getFormErrors(`fields.${selectedFieldRow.name}.dateFormat`)}
                    >
                        <Select onChange={(e) => onDateFormatChange(e, selectedFieldRow)} {...(() => (readOnly ? { open: false } : {}))()}>
                            {dateFormatOptions.map((format) => (
                                <Option key={format.value} value={format.value}>
                                    {format.name}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Segment>
                <Segment className="form-background">
                    <Form.Item
                        label="Date Separator"
                        name={["fields", selectedFieldRow.name, "dateSeparator"]}
                        initialValue={initialDateSeparator}
                        {...getFormErrors(`fields.${selectedFieldRow.name}.dateSeparator`)}
                    >
                        <Select onChange={(e) => onDateSeparatorChange(e, selectedFieldRow)} {...(() => (readOnly ? { open: false } : {}))()}>
                            {Object.keys(dateSeparators).map((key) => (
                                <Option key={key} value={key}>
                                    {dateSeparators[key].name}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Segment>
                <Segment className="form-background">
                    <Form.Item label="Range" name={["fields", selectedFieldRow.name, "range"]} {...getFormErrors(`fields.${selectedFieldRow.name}.range`)}>
                        <RangePicker defaultValue={initialRangeValues} key={`${selectedFieldRow.name}-${rangeFormat}`} format={rangeFormat} />
                    </Form.Item>
                </Segment>
                <Segment className="form-background">
                    <SchemaClassifiersFieldProperty selectedFieldRow={selectedFieldRow} readOnly={readOnly} getFormErrors={getFormErrors}></SchemaClassifiersFieldProperty>
                </Segment>
                <Segment className="form-background">
                    <Form.Item label="Multi Value" name={["fields", selectedFieldRow.name, "isMultivalue"]} valuePropName="checked" initialValue={false}>
                        <Checkbox disabled={readOnly} data-testid={CypressTestIds.SCHEMA_MULTIVALUE_PROPERTY_CHECKBOX}></Checkbox>
                    </Form.Item>
                </Segment>
                <Segment className="form-background">
                    <Form.Item label="PII" name={["fields", selectedFieldRow.name, "isPII"]} valuePropName="checked" initialValue={false}>
                        <Checkbox disabled={readOnly} data-testid={CypressTestIds.SCHEMA_PII_PROPERTY_CHECKBOX}>
                            <Tooltip title="Flag this field as Personally Identifiable Information or Sensitive Data">
                                <InfoCircleTwoTone />
                            </Tooltip>
                        </Checkbox>
                    </Form.Item>
                </Segment>
            </Segment.Group>
        </>
    );
};

export default DateFieldProperties;
